// ============================================================
// DESIGN SYSTEM — Tokens operacionais Canteiro Pro
// Centro operacional enterprise de construção civil
// ============================================================

// ─── Severity palette ────────────────────────────────────────
export const SEVERITY = {
  emergency: {
    bg:       'rgba(248, 113, 113, 0.08)',
    border:   'rgba(248, 113, 113, 0.30)',
    text:     '#f87171',
    dot:      '#ef4444',
    glow:     'rgba(239, 68, 68, 0.15)',
    pulse:    true,
  },
  critical: {
    bg:       'rgba(248, 113, 113, 0.06)',
    border:   'rgba(248, 113, 113, 0.22)',
    text:     '#f87171',
    dot:      '#f87171',
    glow:     'rgba(248, 113, 113, 0.10)',
    pulse:    false,
  },
  warning: {
    bg:       'rgba(251, 191, 36, 0.06)',
    border:   'rgba(251, 191, 36, 0.22)',
    text:     '#fbbf24',
    dot:      '#fbbf24',
    glow:     'rgba(251, 191, 36, 0.08)',
    pulse:    false,
  },
  info: {
    bg:       'rgba(99, 179, 237, 0.06)',
    border:   'rgba(99, 179, 237, 0.18)',
    text:     '#63b3ed',
    dot:      '#63b3ed',
    glow:     'rgba(99, 179, 237, 0.06)',
    pulse:    false,
  },
  success: {
    bg:       'rgba(52, 211, 153, 0.06)',
    border:   'rgba(52, 211, 153, 0.20)',
    text:     '#34d399',
    dot:      '#34d399',
    glow:     'rgba(52, 211, 153, 0.08)',
    pulse:    false,
  },
} as const

export type SeverityKey = keyof typeof SEVERITY

// ─── Risk level palette ──────────────────────────────────────
export const RISK = {
  low:      { bg: 'rgba(52,211,153,0.05)',  border: 'rgba(52,211,153,0.2)',  text: '#34d399', glow: 'rgba(52,211,153,0.08)'  },
  moderate: { bg: 'rgba(251,191,36,0.06)',  border: 'rgba(251,191,36,0.22)', text: '#fbbf24', glow: 'rgba(251,191,36,0.08)'  },
  high:     { bg: 'rgba(251,146,60,0.07)',  border: 'rgba(251,146,60,0.25)', text: '#fb923c', glow: 'rgba(251,146,60,0.12)'  },
  critical: { bg: 'rgba(248,113,113,0.08)', border: 'rgba(248,113,113,0.3)', text: '#f87171', glow: 'rgba(248,113,113,0.15)' },
} as const

// ─── Surface tokens ──────────────────────────────────────────
export const SURFACE = {
  base:      'hsl(222deg 47% 6%)',
  elevated:  'hsl(222deg 47% 8%)',
  card:      'hsl(222deg 47% 9%)',
  overlay:   'hsl(222deg 47% 11%)',
  border:    'rgba(255,255,255,0.06)',
  divider:   'rgba(255,255,255,0.04)',
  highlight: 'rgba(255,255,255,0.04)',
} as const

// ─── Text tokens ─────────────────────────────────────────────
export const TEXT = {
  primary:   '#ffffff',
  secondary: 'hsl(220 15% 65%)',
  muted:     'hsl(220 15% 45%)',
  faint:     'hsl(220 15% 30%)',
  accent:    'hsl(24 95% 55%)',
} as const

// ─── Spacing ─────────────────────────────────────────────────
export const SPACE = {
  xs:  4,
  sm:  8,
  md:  16,
  lg:  24,
  xl:  32,
  '2xl': 48,
} as const

// ─── Border radius ───────────────────────────────────────────
export const RADIUS = {
  sm:   8,
  md:   12,
  lg:   16,
  xl:   20,
  full: 9999,
} as const

// ─── Motion tokens ───────────────────────────────────────────
export const MOTION = {
  fast:    { duration: 0.15, ease: 'easeOut' as const },
  normal:  { duration: 0.25, ease: 'easeOut' as const },
  slow:    { duration: 0.4,  ease: 'easeInOut' as const },
  spring:  { type: 'spring' as const, stiffness: 260, damping: 20 },
  bar:     { duration: 1.0, ease: 'easeOut' as const },
  gauge:   { duration: 1.5, ease: 'easeOut' as const },
  counter: { duration: 1.2, ease: 'easeOut' as const },
} as const

// ─── Z-index layers ──────────────────────────────────────────
export const Z = {
  base:      0,
  card:      10,
  overlay:   20,
  modal:     30,
  toast:     40,
  broadcast: 50,
  emergency: 100,
} as const

// ─── Category brand colors ───────────────────────────────────
export const CATEGORY_COLOR: Record<string, string> = {
  safety:     '#f87171',
  production: '#60a5fa',
  supply:     '#a78bfa',
  weather:    '#38bdf8',
  equipment:  '#fb923c',
  quality:    '#34d399',
  personnel:  '#fbbf24',
  planning:   '#818cf8',
  ai_prediction: 'hsl(24 95% 55%)',
}

// ─── Typography scale ────────────────────────────────────────
export const TYPE = {
  label:   { size: '10px', weight: 700,  tracking: '0.08em', upper: true  },
  caption: { size: '11px', weight: 500,  tracking: '0.02em', upper: false },
  body:    { size: '13px', weight: 400,  tracking: '0',      upper: false },
  metric:  { size: '32px', weight: 900,  tracking: '-0.02em',upper: false },
  hero:    { size: '48px', weight: 900,  tracking: '-0.03em',upper: false },
} as const
